// src/components/ui/FileUpload.jsx — File upload & preview component (Single & Multiple support)
import { useState, useRef } from 'react';
import { uploadApi } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, FileText, Image as ImageIcon, X, ExternalLink, Loader2, Link as LinkIcon, CheckCircle2, Plus, Trash2 } from 'lucide-react';

export default function FileUpload({
  value = '',
  onChange,
  multiple = false,
  label = 'Upload Document / Receipt / Ticket',
  accept = 'image/*,application/pdf,.doc,.docx',
  maxSizeMb = 10,
  className = '',
  hint = 'Supports PNG, JPG, WEBP, PDF up to 10MB',
}) {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const fileInputRef = useRef(null);

  // Normalize file list for multiple mode
  const fileList = multiple
    ? (Array.isArray(value) ? value.filter(Boolean) : (typeof value === 'string' && value ? [value] : []))
    : [];

  const getFullUrl = (urlStr) => {
    if (!urlStr) return '';
    if (urlStr.startsWith('http://') || urlStr.startsWith('https://') || urlStr.startsWith('data:')) {
      return urlStr;
    }
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const serverHost = apiBase.replace(/\/api\/?$/, '');
    return `${serverHost}${urlStr.startsWith('/') ? '' : '/'}${urlStr}`;
  };

  const uploadSingleFile = async (file) => {
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`"${file.name}" size exceeds limit of ${maxSizeMb}MB`);
      return null;
    }
    try {
      const res = await uploadApi.uploadFile(file);
      return res.url || res.data_url;
    } catch (err) {
      console.error('File upload error:', err);
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (evt) => resolve(evt.target.result);
        reader.readAsDataURL(file);
      });
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    try {
      if (multiple) {
        const newUrls = [];
        for (const f of files) {
          const url = await uploadSingleFile(f);
          if (url) newUrls.push(url);
        }
        if (newUrls.length) {
          onChange([...fileList, ...newUrls]);
          toast.success(`${newUrls.length} file(s) attached!`);
        }
      } else {
        const file = files[0];
        const url = await uploadSingleFile(file);
        if (url) {
          onChange(url);
          toast.success('File uploaded successfully!');
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleManualUrlSave = (e) => {
    e.preventDefault();
    if (manualUrl.trim()) {
      if (multiple) {
        onChange([...fileList, manualUrl.trim()]);
      } else {
        onChange(manualUrl.trim());
      }
      setShowUrlInput(false);
      setManualUrl('');
      toast.success('File URL saved');
    }
  };

  const handleRemoveSingle = () => {
    onChange(multiple ? [] : '');
    setShowUrlInput(false);
  };

  const handleRemoveIndex = (index) => {
    if (multiple) {
      const updated = fileList.filter((_, idx) => idx !== index);
      onChange(updated);
    }
  };

  const renderFileCard = (urlStr, index = null) => {
    const fullUrl = getFullUrl(urlStr);
    const isImage = urlStr && (
      urlStr.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) ||
      urlStr.startsWith('data:image/')
    );
    const isPdf = urlStr && (
      urlStr.match(/\.pdf($|\?)/i) ||
      urlStr.startsWith('data:application/pdf')
    );
    const fileName = urlStr.split('/').pop().split('?')[0] || urlStr;

    return (
      <div key={index !== null ? index : urlStr} className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 flex items-center justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-3 overflow-hidden min-w-0">
          {isImage ? (
            <div className="w-10 h-10 rounded-lg border border-slate-200 bg-white shrink-0 overflow-hidden flex items-center justify-center">
              <img src={fullUrl} alt="Attachment" className="w-full h-full object-cover" />
            </div>
          ) : isPdf ? (
            <div className="w-10 h-10 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 shrink-0 flex items-center justify-center font-bold text-xs">
              PDF
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 shrink-0 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
          )}

          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 truncate">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="truncate">{fileName}</span>
            </div>
            <p className="text-[11px] text-slate-400 truncate mt-0.5 max-w-[240px]">
              {urlStr.length > 50 ? `${urlStr.substring(0, 45)}...` : urlStr}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition-colors flex items-center gap-1.5 shadow-xs"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-500" /> View
          </a>
          {multiple ? (
            <button
              type="button"
              onClick={() => handleRemoveIndex(index)}
              className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
              title="Remove file"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
            >
              Change
            </button>
          )}
        </div>
      </div>
    );
  };

  // MULTIPLE MODE
  if (multiple) {
    return (
      <div className={`space-y-3 ${className}`}>
        {label && (
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
              {label} ({fileList.length})
            </label>
            {fileList.length > 0 && (
              <button
                type="button"
                onClick={handleRemoveSingle}
                className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear All
              </button>
            )}
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={accept}
          multiple
          className="hidden"
        />

        {/* Render attached file list */}
        {fileList.length > 0 && (
          <div className="space-y-2">
            {fileList.map((url, idx) => renderFileCard(url, idx))}
          </div>
        )}

        {showUrlInput ? (
          <form onSubmit={handleManualUrlSave} className="flex gap-2">
            <input
              type="url"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder="Paste document / receipt URL (e.g. https://...)"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              autoFocus
            />
            <button type="submit" className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
              Save
            </button>
            <button type="button" onClick={() => setShowUrlInput(false)} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-100">
              Cancel
            </button>
          </form>
        ) : (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-2.5 px-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 hover:bg-blue-50/50 hover:border-blue-400 transition-all flex items-center justify-center gap-2 text-xs font-semibold text-slate-700 hover:text-blue-700"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 text-blue-600 animate-spin" /> Uploading...</>
              ) : (
                <><Plus className="w-4 h-4 text-blue-600" /> Add Attachment (PDF, Image, Voucher)</>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-[11px] font-medium text-slate-500 hover:text-blue-600 hover:bg-slate-50 flex items-center justify-center gap-1"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Paste URL
            </button>
          </div>
        )}
      </div>
    );
  }

  // SINGLE MODE (existing)
  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
            {label}
          </label>
          {value && (
            <button
              type="button"
              onClick={handleRemoveSingle}
              className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={accept}
        className="hidden"
      />

      {value ? (
        renderFileCard(value)
      ) : showUrlInput ? (
        <form onSubmit={handleManualUrlSave} className="flex gap-2">
          <input
            type="url"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="Paste document / receipt URL (e.g. https://...)"
            className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            autoFocus
          />
          <button type="submit" className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
            Save
          </button>
          <button type="button" onClick={() => setShowUrlInput(false)} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-100">
            Cancel
          </button>
        </form>
      ) : (
        <div className="relative">
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`group cursor-pointer rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 hover:bg-blue-50/50 hover:border-blue-400 transition-all p-4 text-center ${
              uploading ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center justify-center py-2 gap-2">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                <span className="text-xs font-semibold text-slate-700">Uploading file...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1.5">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Upload className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800">
                    <span className="text-blue-600 hover:underline">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="text-[11px] text-slate-500 hover:text-blue-600 flex items-center gap-1 font-medium"
            >
              <LinkIcon className="w-3 h-3" /> Or paste external link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
